import { useEffect, useState, type ReactNode } from 'react';
import { Callout, SelectField } from '@humanforce/mango-ui-react';
import { useLocation, useNavigate } from 'react-router';
import { listOrganisations } from '../features/organisation/organisationApi';
import { ClearingHouseConfigurationScreen, SchemeConfigurationScreen } from '../features/superConfiguration/SuperConfigurationScreens';
import { TabbedItemPage } from './TabbedItemPage';
import { createTenantSuperannuationSystemParametersClient, SuperannuationSystemParametersScreen } from '@pay-roadrunner/superannuation-system-parameters-ui';
import { authFetch } from '../auth/authFetch';
import { getApiBaseUrl } from '../auth/api';

const systemParametersClient = createTenantSuperannuationSystemParametersClient({ getBaseUrl: getApiBaseUrl, fetchImpl: authFetch });

interface BusinessRecordScope { businessRecordId: string; inactive: boolean }

const BusinessRecordScopedConfiguration = ({ children }: { children: (scope: BusinessRecordScope) => ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryId = new URLSearchParams(location.search).get('businessRecordId') ?? '';
  const [businessRecordId, setBusinessRecordId] = useState(queryId);
  const [options, setOptions] = useState<{ label: string; value: string; inactive: boolean }[]>([]);
  const [error, setError] = useState('');

  useEffect(() => { void listOrganisations().then(({ orgIds, orgs }) => {
    const values = orgIds.flatMap((id) => orgs[id].businessRecords.flatMap((record) => record.businessRecordId ? [{ value: record.businessRecordId, label: record.legal.legalName, inactive: record.status === 'inactive' }] : []));
    setOptions(values);
    setBusinessRecordId((current) => current || values[0]?.value || '');
  }).catch(() => setError('Business Records could not be loaded.')); }, []);

  useEffect(() => {
    if (!businessRecordId || queryId === businessRecordId) return;
    const params = new URLSearchParams(location.search);
    params.set('businessRecordId', businessRecordId);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [businessRecordId, location.pathname, location.search, navigate, queryId]);

  const selected = options.find((option) => option.value === businessRecordId);
  return <div className="mgo-stack mgo-gap-l"><SelectField label="Business Record" required searchable value={businessRecordId} options={options} onChange={(value) => setBusinessRecordId(typeof value === 'string' ? value : '')} />{error && <Callout tone="critical">{error}</Callout>}{children({ businessRecordId, inactive: selected?.inactive ?? false })}</div>;
};

export const MaintenanceSuperannuationPage = () => (
  <TabbedItemPage
    itemPath="/maintenance/superannuation"
    preserveSearch
    tabContentOverrides={{
      'clearing-house-configuration': <BusinessRecordScopedConfiguration>{({ businessRecordId }) => <ClearingHouseConfigurationScreen businessRecordId={businessRecordId} />}</BusinessRecordScopedConfiguration>,
      'scheme-configuration': <BusinessRecordScopedConfiguration>{({ businessRecordId, inactive }) => <SchemeConfigurationScreen businessRecordId={businessRecordId} readOnly={inactive} />}</BusinessRecordScopedConfiguration>,
      'system-parameters': <BusinessRecordScopedConfiguration>{({ businessRecordId }) => <SuperannuationSystemParametersScreen client={systemParametersClient} businessRecordId={businessRecordId} readOnly />}</BusinessRecordScopedConfiguration>,
    }}
  />
);
